<template>
    <v-dialog
        :value="isOpen"
        max-width="1200px"
        transition="slide-y-transition"
        eager
        @keydown.esc="onClose"
        @click:outside="onClose"
    >
        <v-card>
            <v-card-title class="py-3">
                <v-text-field
                    v-model="localizationSearch"
                    outlined
                    dense
                    hide-details
                    @keydown.enter="findCoordinates"
                />
                <v-btn
                    color="secondary"
                    class="ml-3"
                    depressed
                    :loading="actionLoading"
                    :disabled="!hasSearchValue"
                    data-sentry-label="Znajdź"
                    @click="findCoordinates"
                >
                    Znajdź
                </v-btn>
            </v-card-title>

            <v-divider />

            <div
                ref="map"
                class="google-map"
                :style="{height: height + 'px'}"
                @contextmenu="showContextMenu"
            />

            <v-divider />

            <v-card-actions>
                <v-btn
                    color="error"
                    text
                    data-sentry-label="Anuluj"
                    @click="onClose()"
                >
                    Anuluj
                </v-btn>
                <v-spacer />
                <v-btn
                    color="success"
                    text
                    data-sentry-label="Zapisz"
                    @click="saveCoordinates"
                >
                    Zapisz
                </v-btn>
            </v-card-actions>
        </v-card>

        <v-menu
            v-model="contextMenu.isVisible"
            :position-x="contextMenu.x"
            :position-y="contextMenu.y"
            absolute
            offset-y
        >
            <v-list dense>
                <v-list-item
                    @click="addMarker({
                        lat: contextMenu.latitude,
                        lng: contextMenu.longitude
                    })"
                >
                    <v-list-item-title>Upuść marker</v-list-item-title>
                </v-list-item>
            </v-list>
        </v-menu>
    </v-dialog>
</template>

<script>
import { errorHandler } from '@/utils/errorHandler'
import { appConfig } from '@/config'
import redPin from '@/assets/images/maps/pin-red.svg'

export default {
    name: 'MapDialog',
    props: {
        isOpen: {
            type: Boolean
        },
        localizationData: {
            type: Object,
            default: null
        },
        google: {
            type: Object,
            default: null
        },
        height: {
            type: Number,
            default: 0
        }
    },
    data() {
        return {
            localizationSearch: '',
            map: null,
            geocoder: null,
            markers: [],
            latitude: null,
            longitude: null,
            contextMenu: {
                isVisible: false,
                x: 0,
                y: 0,
                latitude: 0,
                longitude: 0
            },
            actionLoading: false
        }
    },
    computed: {
        hasSearchValue() {
            return !!this.localizationSearch
        },
        hasCoordinates() {
            return !!(this.localizationData.latitude && this.localizationData.longitude)
        }
    },
    watch: {
        isOpen(boolean) {
            if (boolean) {
                this.initMap()
                if (this.localizationData.street && this.localizationData.city) {
                    this.localizationSearch = `${this.localizationData.street}, ${this.localizationData.postCode} ${this.localizationData.city}`
                }
            }
        }
    },
    methods: {
        initMap() {
            const mapContainer = this.$refs.map

            this.geocoder = new this.google.maps.Geocoder()

            this.map = new this.google.maps.Map(mapContainer, {
                center: this.hasCoordinates ?
                    {
                        lat: this.localizationData.latitude,
                        lng: this.localizationData.longitude
                    } :
                    null,
                zoom: this.hasCoordinates ? appConfig.outdoors.map.zoom : null,
                mapId: appConfig.outdoors.map.mapId
            })

            if (!this.hasCoordinates) {
                const bounds = new this.google.maps.LatLngBounds(
                    new this.google.maps.LatLng(appConfig.outdoors.map.countryBounds.southWestLat, appConfig.outdoors.map.countryBounds.southWestLng),
                    new this.google.maps.LatLng(appConfig.outdoors.map.countryBounds.northEastLat, appConfig.outdoors.map.countryBounds.northEastLng) )

                this.map.fitBounds(bounds)
            }

            this.map.addListener('rightclick', event => {
                this.contextMenu.latitude = event.latLng.lat()
                this.contextMenu.longitude = event.latLng.lng()
            })

            if (this.hasCoordinates) {
                const position = {
                    lat: this.localizationData.latitude,
                    lng: this.localizationData.longitude
                }

                this.addMarker(position)
            }
        },
        addMarker(position) {
            this.deleteAllMarkers()

            const marker = new this.google.maps.Marker({
                position: position,
                map: this.map,
                draggable: true,
                animation: this.google.maps.Animation.DROP,
                icon: redPin,
            })

            this.setCoordinates(position.lat, position.lng)

            marker.addListener('dragend', event => {
                this.setCoordinates(
                    event.latLng.lat(),
                    event.latLng.lng()
                )
            })

            this.markers.push(marker)
        },
        deleteAllMarkers() {
            this.markers.forEach((item, index) => {
                this.markers[index].setMap(null)
            })

            this.markers = []
        },
        findCoordinates() {
            this.actionLoading = true

            this.geocoder.geocode({
                'address': this.localizationSearch
            },
            (results, status) => {
                this.actionLoading = false
                if (status == 'OK') {
                    const position = results[0].geometry.location

                    this.addMarker({
                        lat: position.lat(),
                        lng: position.lng(),
                    })
                    this.map.setCenter(position)
                    this.map.setZoom(appConfig.outdoors.map.zoom)
                } else {
                    if (status == 'ZERO_RESULTS') {
                        this.$store.dispatch('showNotification', {
                            type: 'error',
                            message: 'Nie znaleziono adresu.'
                        })
                    } else {
                        this.$store.dispatch('showNotification', {
                            type: 'error',
                            message: 'Nieokreślony błąd wyszukiwania.'
                        })
                        errorHandler(status)
                    }
                }
            })
        },
        showContextMenu(event) {
            this.contextMenu.isVisible = false
            this.contextMenu.x = event.clientX
            this.contextMenu.y = event.clientY
            this.$nextTick(() => {
                this.contextMenu.isVisible = true
            })
        },
        setCoordinates(latitude, longitude) {
            this.latitude = parseFloat(latitude.toPrecision(8))
            this.longitude = parseFloat(longitude.toPrecision(8))
        },
        saveCoordinates() {
            this.$emit('on-save', this.latitude, this.longitude)
            this.onClose()
        },
        resetComponent() {
            this.deleteAllMarkers()
            this.geocoder = null
            this.map = null
            this.latitude = null
            this.longitude = null
            this.localizationSearch = ''
        },
        onClose() {
            setTimeout(() => {
                this.resetComponent()
            }, 300)
            this.$emit('on-close')
        }
    }
}
</script>
